import React, { useState, useEffect, useCallback } from 'react';
import AdminDataService, { User, UserActivity, AdminStats } from '../services/AdminDataService';
import { AlertService } from '../services/AlertService';
import { ConfirmationDialog } from './ConfirmationDialog';
import { UserFilters } from './UserFilters';
import { LoadingScreen } from './LoadingScreen';
import { useAuth } from '../context/AuthContext';
import { useUserFilters } from '../hooks/useUserFilters';
import { useLoading } from '../hooks/useLoading';
import './UserManagement.css';

interface UserManagementProps {}

/**
 * UserManagement Component - Embedded version for AdminPanel
 * 
 * Purpose: Streamlined user management component for embedding in other interfaces
 * Features: User list, editing, admin stats, role management
 * Does NOT include: Invite management, full page layout
 * 
 * Used in: AdminPanel.tsx as a tab component  
 * Route: /admin -> Users tab
 * CSS: Uses .admin-user-management scope to avoid conflicts
 * State: Independent from pages version, manages own loading/data states
 * 
 * IMPORTANT: Keep this focused on user management only. 
 * Invite functionality belongs in pages/UserManagement.tsx
 */
export const UserManagement: React.FC<UserManagementProps> = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roleFilteredUsers, setRoleFilteredUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [error, setError] = useState<string>('');
  
  // Loading state management
  const { isLoading, startLoading, stopLoading, loadingMessage, withLoading } = useLoading('Initializing user management...');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showModal, setShowModal] = useState<'edit' | 'delete' | 'activities' | null>(null);
  const [editFormData, setEditFormData] = useState({ role: '' });
  const [availableRoles, setAvailableRoles] = useState<{ value: string; label: string }[]>([]);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    confirmButtonClass?: string;
    onConfirm: () => void;
    loading?: boolean;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const adminService = AdminDataService.getInstance();
  const alertService = AlertService;
  const { user: currentUser } = useAuth();

  // Initialize filter system with role-filtered users
  const filterOptions = {
    availableRoles: availableRoles.map(role => role.value),
    availableSupervisors: [] as Array<{ id: string; name: string; role: string }>
  };
  
  const {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filteredUsers,
    uniqueRoles,
    filterStats
  } = useUserFilters(roleFilteredUsers, filterOptions);

  // Filter users based on current user's role and exclude self
  const filterUsersForCurrentUser = useCallback((allUsers: User[]) => {
    if (!currentUser) return allUsers;

    // Always exclude the current user from the management list
    const otherUsers = allUsers.filter(user => user.id !== currentUser.id);
    
    // Role hierarchy filtering rules:
    // - Admin: Can manage all roles (including other admins and directors)
    // - Director: Can manage all roles except admin (but can see other directors)
    // - Other roles: Based on your business rules, but typically can't access user management
    
    if (currentUser.role === 'admin') {
      // Admin can see and manage all users except themselves
      return otherUsers;
    } else if (currentUser.role === 'director') {
      // Director can manage all roles except admin, but can see other directors
      return otherUsers.filter(user => user.role !== 'admin');
    } else {
      // Other roles typically shouldn't access user management, but if they do:
      // They can only see users with lower roles
      const managedRoles = ['agent', 'sales_team_lead', 'processor_agent', 'processor_team_lead'];
      return otherUsers.filter(user => managedRoles.includes(user.role));
    }
  }, [currentUser]);

  const loadData = useCallback(async () => {
    await withLoading(async () => {
      try {
        setError('');
        console.log('🔍 Starting to load users data...');
        
        const [usersData, statsData] = await Promise.all([
          adminService.getUsers(true),
          adminService.getAdminStats(true)
        ]);
        
        console.log('📊 Raw users data:', usersData);
        console.log('📈 Stats data:', statsData);
        
        setUsers(usersData);
        // Filter users based on current user's role and exclude self
        const filtered = filterUsersForCurrentUser(usersData);
        console.log('🎯 Filtered users (after role filtering):', filtered);
        console.log('👤 Current user:', currentUser);
        
        setRoleFilteredUsers(filtered);
        setStats(statsData);
      } catch (err: any) {
        console.error('❌ Error loading users:', err);
        setError(err.message);
        throw err; // Re-throw to let withLoading handle it properly
      }
    }, 'Loading user data and statistics...');
  }, [adminService, filterUsersForCurrentUser, withLoading]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditUser = async (user: User) => {
    await withLoading(async () => {
      try {
        // Fetch available roles
        const roles = await adminService.getUserRoles();
        setAvailableRoles(roles);
        
        setSelectedUser(user);
        setEditFormData({
          role: user.role
        });
        setShowModal('edit');
      } catch (err: any) {
        setError('Failed to load roles: ' + err.message);
        throw err;
      }
    }, 'Loading user roles...');
  };

  const handleDeleteUser = (user: User) => {
    setConfirmationDialog({
      isOpen: true,
      title: 'Remove User Account',
      message: `Are you sure you want to remove user "${user.username}" (${user.full_name || 'N/A'}) from active management? This action will:\n\n• Deactivate the user account\n• Remove user from management interface\n• Revoke all active sessions\n• Preserve all data for audit purposes\n\nThe user will no longer be able to access the system, but their data will be retained.\n\nEmail: ${user.email}\nRole: ${user.role}\nStatus: ${user.is_active ? 'Active' : 'Inactive'}`,
      confirmText: 'Remove User',
      confirmButtonClass: 'confirm-button warning',
      onConfirm: () => confirmDeleteUser(user)
    });
  };

  const confirmDeleteUser = async (user: User) => {
    setConfirmationDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await adminService.deleteUser(user.id);
      alertService.success(`User ${user.username} has been removed from active management.`);
      await loadData(); // Refresh data
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    } catch (err: any) {
      alertService.error(`Failed to remove user: ${err.message}`);
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    }
  };

  const handleViewActivities = async (user: User) => {
    await withLoading(async () => {
      try {
        setSelectedUser(user);
        const userActivities = await adminService.getUserActivities(user.id, true);
        setActivities(userActivities);
        setShowModal('activities');
      } catch (err: any) {
        setError(err.message);
        throw err;
      }
    }, `Loading activities for ${user.username}...`);
  };

  const handleToggleUserStatus = (user: User) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    const status = user.is_active ? 'inactive' : 'active';
    
    setConfirmationDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      message: `Are you sure you want to ${action} user "${user.username}" (${user.full_name || 'N/A'})? This will change their status to ${status} and ${user.is_active ? 'prevent them from accessing' : 'allow them to access'} the system.`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      confirmButtonClass: user.is_active ? 'confirm-button warning' : 'confirm-button',
      onConfirm: () => confirmToggleUserStatus(user)
    });
  };

  const confirmToggleUserStatus = async (user: User) => {
    setConfirmationDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await adminService.toggleUserStatus(user.id);
      await loadData(); // Refresh data
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      alertService.success(`User ${user.username} has been ${user.is_active ? 'activated' : 'deactivated'} successfully.`);
    } catch (err: any) {
      alertService.error(`Failed to ${user.is_active ? 'deactivate' : 'activate'} user: ${err.message}`);
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    }
  };

  const handleResetPassword = (user: User) => {
    setConfirmationDialog({
      isOpen: true,
      title: 'Reset User Password',
      message: `Are you sure you want to reset the password for user "${user.username}" (${user.full_name || 'N/A'})? This will:\n\ni• Send reset instructions to ${user.email}\n• Require the user to set a new password on next login\n\nThe user will receive an email with password reset instructions.`,
      confirmText: 'Reset Password',
      confirmButtonClass: 'confirm-button warning',
      onConfirm: () => confirmResetPassword(user)
    });
  };

  const confirmResetPassword = async (user: User) => {
    setConfirmationDialog(prev => ({ ...prev, loading: true }));
    
    try {
      const result = await adminService.resetUserPassword(user.id);
      alertService.success(`Password reset initiated successfully for ${user.username}. ${result.message}`);
      await loadData(); // Refresh data
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    } catch (err: any) {
      alertService.error(`Failed to reset password: ${err.message}`);
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    }
  };

  const confirmEdit = () => {
    if (!selectedUser) return;

    setConfirmationDialog({
      isOpen: true,
      title: 'Update User Role',
      message: `Are you sure you want to change the role for user "${selectedUser.username}" (${selectedUser.full_name || 'N/A'}) from "${selectedUser.role}" to "${editFormData.role}"?\n\nThis will:\n• Update their system permissions\n• Change their access level\n• Take effect immediately`,
      confirmText: 'Update Role',
      confirmButtonClass: 'confirm-button',
      onConfirm: () => confirmUpdateRole(selectedUser, editFormData.role)
    });
  };

  const confirmUpdateRole = async (user: User, newRole: string) => {
    setConfirmationDialog(prev => ({ ...prev, loading: true }));
    
    try {
      await adminService.updateUserRole(
        user.id,
        newRole,
        user.is_active // Keep current active status
      );
      alertService.success(`User role updated successfully from ${user.role} to ${newRole}.`);
      setShowModal(null);
      await loadData(); // Refresh data
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    } catch (err: any) {
      alertService.error(`Failed to update user role: ${err.message}`);
      setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} });
    }
  };


  const closeModal = () => {
    setShowModal(null);
    setSelectedUser(null);
    setActivities([]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin': return 'role-admin';
      case 'director': return 'role-director';
      case 'staff': return 'role-staff';
      case 'agent': return 'role-agent';
      case 'sales_team_lead': return 'role-sales-team-lead';
      case 'processor_agent': return 'role-processor-agent';
      case 'processor_team_lead': return 'role-processor-team-lead';
      case 'floor_manager': return 'role-floor-manager';
      default: return 'role-default';
    }
  };

  return (
    <div className="admin-user-management">
      <div className="user-management-header">
        <h2>User Management</h2>
        <button 
          onClick={loadData} 
          className="btn btn-outline-primary btn-sm refresh-button"
          disabled={isLoading}
        >
          🔄 Refresh
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Statistics Dashboard */}
      {stats && (
        <div className="admin-stats">
          <div className="stat-card">
            <div className="stat-number">{stats.total_users}</div>
            <div className="stat-label">Total Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.active_users}</div>
            <div className="stat-label">Active Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.inactive_users}</div>
            <div className="stat-label">Inactive Users</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.recent_registrations}</div>
            <div className="stat-label">Recent Signups</div>
          </div>
        </div>
      )}

      {/* User Filters */}
      <UserFilters
        filters={filters}
        updateFilter={updateFilter}
        clearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        uniqueRoles={uniqueRoles}
        availableSupervisors={[]}
        filterStats={filterStats}
      />

      {/* Users Table */}
      <div className="users-table-container">
        <div className="table-header">
          <h3>
            Manageable Users 
            {hasActiveFilters && (
              <span className="filtered-indicator">
                ({filteredUsers.length} of {roleFilteredUsers.length})
              </span>
            )}
            {!hasActiveFilters && (
              <span className="total-count">({filteredUsers.length})</span>
            )}
          </h3>
          {currentUser && (
            <p className="table-subtitle">
              Showing users you can manage as <strong>{currentUser.role}</strong> (excluding yourself)
              {hasActiveFilters && (
                <span className="filter-active-indicator"> • Filters applied</span>
              )}
            </p>
          )}
        </div>
        
        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            {hasActiveFilters ? (
              <p>No users match your current filters.</p>
            ) : (
              <>
                <p>No users available for management.</p>
                {currentUser?.role === 'director' && (
                  <p className="note">Directors cannot manage Admin users.</p>
                )}
              </>
            )}
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>{user.full_name || 'N/A'}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                    {user.role}
                  </span>
                </td>
                <td>
                  <label className="toggle-switch" title={`Click to ${user.is_active ? 'deactivate' : 'activate'} user`}>
                    <input
                      type="checkbox"
                      checked={user.is_active}
                      onChange={() => handleToggleUserStatus(user)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </label>
                </td>
                <td>{user.last_login ? formatDate(user.last_login) : 'Never'}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      onClick={() => handleEditUser(user)}
                      className="btn btn-icon btn-icon-sm btn-outline-primary action-button edit-button"
                      title="Edit user"
                    >
                      ✏️
                    </button>
                    <button 
                      onClick={() => handleResetPassword(user)}
                      className="btn btn-icon btn-icon-sm btn-accent action-button reset-button"
                      title="Reset password"
                    >
                      🔑
                    </button>
                    <button 
                      onClick={() => handleViewActivities(user)}
                      className="btn btn-icon btn-icon-sm btn-secondary action-button activity-button"
                      title="View activities"
                    >
                      📊
                    </button>
                    <button 
                      onClick={() => handleDeleteUser(user)}
                      className="btn btn-icon btn-icon-sm btn-danger action-button delete-button"
                      title="Delete user"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit User Modal */}
      {showModal === 'edit' && selectedUser && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit User: {selectedUser.username}</h3>
            <div className="form-group">
              <label>Role:</label>
              <select 
                value={editFormData.role}
                onChange={(e) => setEditFormData({...editFormData, role: e.target.value})}
              >
                {availableRoles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <p className="note-text">
                <strong>Note:</strong> Use the toggle button in the table to change active/inactive status.
              </p>
            </div>
            <div className="modal-actions">
              <button onClick={confirmEdit} className="btn btn-primary confirm-button">Save Changes</button>
              <button onClick={closeModal} className="btn btn-outline-secondary cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}


      {/* User Activities Modal */}
      {showModal === 'activities' && selectedUser && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Activity Log: {selectedUser.username}</h3>
            <div className="activities-list">
              {activities.length > 0 ? (
                <table className="activities-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Details</th>
                      <th>IP Address</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map(activity => (
                      <tr key={activity.id}>
                        <td>{activity.action}</td>
                        <td>{activity.details}</td>
                        <td>{activity.ip_address || 'N/A'}</td>
                        <td>{formatDate(activity.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No activities found for this user.</p>
              )}
            </div>
            <div className="modal-actions">
              <button onClick={closeModal} className="btn btn-outline-secondary cancel-button">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        confirmButtonClass={confirmationDialog.confirmButtonClass}
        loading={confirmationDialog.loading}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={() => setConfirmationDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
      />
      
      {/* Loading Screen Overlay */}
      <LoadingScreen isVisible={isLoading} message={loadingMessage} />
    </div>
  );
};